/**
 * Created by Alex Xia on 08/03/2019.
 * Description:
 *  Float button
 * ------ maintenance history ------
 */
import { Component, OnInit, Input, ElementRef, Renderer2, OnDestroy, ViewChild, AfterViewChecked, ChangeDetectorRef } from '@angular/core';

import { DataList } from '../../tamalelibs/models/data-list.model';
import { Thread } from '../../tamalelibs/models/thread.model';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../redux';
import { FloatMenuService } from '../../services/float-menu.service';
import { ContextMenuConfig, ContextMenuShowAction } from '../../widgets/tam-context-menu/tam-context-menu.model';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NoteDialogOpenOptions, NoteDialogType } from '../note-dialog/note-dialog.model';
import { NoteDialogService } from '../note-dialog/note-dialog.service';
import { DELETE_DRAFT } from '../../tamalelibs/redux/note.actions';
import { LONG_TOUCH_INTERVAL } from '../../tamalelibs/constants/business.constants';
import { SortInfo, QueryInfo, ThreadListConfig } from '../../pages/research/research.view-model';
import { getDraftListState, getDraftListSortInfoState, getDraftListQueryInfoState } from '../../redux/reducers/draft-view.reducer';

export const DEFAULT_ITEM_HEIGHT = 96;

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'draft-list',
    templateUrl: './draft-list.component.html',
    styleUrls: ['./draft-list.component.scss'],
    providers: [
    ]
})
export class DraftListComponent implements OnInit, OnDestroy, AfterViewChecked {
    @Input() config: ThreadListConfig;
    @ViewChild('listContainer', { static: false }) listContainerEle: ElementRef;

    loadingMore = false;
    reloading = false;
    selectedItemMap: Map<string, number> = new Map();
    draftList: DataList<Thread> = new DataList<Thread>();
    contextMenuConfig: ContextMenuConfig;
    isIPAD = false;
    sortInfo: SortInfo;

    private _currentPageIndex = 1;
    private _destroySubscriptions: Array<Subscription> = [];
    private _queryInfo: QueryInfo;
    private _touchstart: number;
    private _element: HTMLElement;

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _store: Store<AppState>,
        private _deviceService: DeviceDetectorService,
        public _floatMenuService: FloatMenuService,
        private _noteDialogService: NoteDialogService,
        private _elementRef: ElementRef,
        private _renderer: Renderer2,
    ) {
        this.isIPAD = this._deviceService.isMobile() || this._deviceService.isTablet();
        this._element = this._elementRef.nativeElement;
    }

    public ngOnInit() {
        this._destroySubscriptions.push(
            this._store.pipe(
                select(getDraftListState)
            ).subscribe(items => this._onSubscribeDraftListItem(items))
        );

        this._destroySubscriptions.push(
            this._store.pipe(
                select(getDraftListSortInfoState)
            ).subscribe(res => this._onSubscribSortInfo(res))
        );

        this._destroySubscriptions.push(
            this._store.pipe(
                select(getDraftListQueryInfoState)
            ).subscribe(res => this._onSubscribQueryInfo(res))
        );

        this.config.actions$.subscribe((action) => this._onSubscribeActions(action));

        this.initContextMenu();
    }

    public ngAfterViewChecked() {
        const draftListClass = '.draft-list-container';
        if (this._element.querySelector(draftListClass) != null) {
            const panelHeight = this._floatMenuService.getHeightOfFloatMenuDraftPanel(this.draftList.length) - 90;
            this._renderer.setStyle(this._element.querySelector(draftListClass), 'height', panelHeight.toString() + 'px');
            this._renderer.setStyle(this._element.querySelector(draftListClass), 'min-height', '260px');
            this._renderer.setStyle(this._element.querySelector(draftListClass), 'max-height', '730px');
        }
    }

    public ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onContextMenu(event, item: Thread, index: number) {
        this._changeDetectorRef.detectChanges();
        this.contextMenuConfig.show$.next(new ContextMenuShowAction(event, item));
    }

    onItemDoubleClick(event, item: Thread) {
        if (event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        if (!item.parentEntry.editable) {
            return;
        }
        const _noteOption = new NoteDialogOpenOptions();
        _noteOption.entryId = item.id;
        _noteOption.openType = NoteDialogType.EditDraft;
        _noteOption.minimizable = true;
        _noteOption.entityFieldEditable = true;

        this._noteDialogService.dialogOpen$.next(_noteOption);
    }

    onItemSelect(item: Thread, index: number) {
        if (this.selectedItemMap.size === 1 && this.selectedItemMap.has(item.id)) {
            return;
        }
        this.selectedItemMap.clear();
        this.selectedItemMap.set(item.id, index);
    }

    onKeydown(event) {
        // use up/down arrow keys to select through items
        if (event.keyCode === 40) {
            // get the index of last selected item
            let nextIndex = 0;
            Array.from(this.selectedItemMap.values()).forEach(index => {
                if (index > nextIndex) {
                    nextIndex = index;
                }
            });
            nextIndex++;
            if (nextIndex < this.draftList.length) {
                if ((nextIndex + 1) * DEFAULT_ITEM_HEIGHT > (this.listContainerEle.nativeElement.clientHeight + this.listContainerEle.nativeElement.scrollTop)) {
                    // handle tha case that selected item below the visible area
                    this.listContainerEle.nativeElement.scrollTop = (nextIndex + 1) * DEFAULT_ITEM_HEIGHT - this.listContainerEle.nativeElement.clientHeight;
                } else if (this.listContainerEle.nativeElement.scrollTop > (nextIndex + 1) * DEFAULT_ITEM_HEIGHT) {
                    // handle the case that selected item above the visible area
                    this.listContainerEle.nativeElement.scrollTop = nextIndex * DEFAULT_ITEM_HEIGHT;
                }

                this.selectedItemMap.clear();
                this.selectedItemMap.set(this.draftList[nextIndex].id, nextIndex);
            }
        } else if (event.keyCode === 38) {
            // get the index of first selected item
            const indexArr = Array.from(this.selectedItemMap.values());
            let nextIndex = indexArr[0];
            indexArr.forEach(index => {
                if (index < nextIndex) {
                    nextIndex = index;
                }
            });
            nextIndex--;
            if (nextIndex >= 0) {
                if ((nextIndex + 1) * DEFAULT_ITEM_HEIGHT > (this.listContainerEle.nativeElement.clientHeight + this.listContainerEle.nativeElement.scrollTop)) {
                    // handle tha case that selected item below the visible area
                    this.listContainerEle.nativeElement.scrollTop = (nextIndex + 1) * DEFAULT_ITEM_HEIGHT - this.listContainerEle.nativeElement.clientHeight;
                } else if (this.listContainerEle.nativeElement.scrollTop > nextIndex * DEFAULT_ITEM_HEIGHT) {
                    // handle the case that selected item above the visible area
                    this.listContainerEle.nativeElement.scrollTop = nextIndex * DEFAULT_ITEM_HEIGHT;
                }

                this.selectedItemMap.clear();
                this.selectedItemMap.set(this.draftList[nextIndex].id, nextIndex);
            }
        }
    }

    onScrolled() {
        this.loadingMore = true;
        this._currentPageIndex++;

        this._loadMoreDraftList();
    }

    showNoData() {
        if (this.draftList.length === 0 && !this.reloading) {
            return 'flex';
        } else {
            return 'none';
        }
    }

    trackByFn(index, item) {
        return item.id;
    }

    // For iPad
    touchstart() {
        this._touchstart = new Date().getTime();
    }

    // For iPad
    touchend(event, item: Thread, index: number) {
        // for delete
        if (this.isIPAD && new Date().getTime() - this._touchstart > LONG_TOUCH_INTERVAL) {
            event.clientX = event.pageX;
            event.clientY = event.pageY;
            this.onContextMenu(event, item, index);
        }
    }

    private _ensureSelectedItemVisible(index) {
        setTimeout(() => {
            const scrollTop = index * DEFAULT_ITEM_HEIGHT;
            this.listContainerEle.nativeElement.scrollTop = scrollTop;
        });
    }

    private _delete(thread) {
        this._store.dispatch({
            type: DELETE_DRAFT,
            payload: {
                id: thread.id,
                componentId: 'draft list',
                draft: true,
            }
        });
    }

    public initContextMenu() {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.stateful = false;
        this.contextMenuConfig.items = [
            { id: '0', text: 'Discard Draft', disabled: false }
        ];
        if (this.isIPAD) {
            this.contextMenuConfig.items.push({ id: '1', text: 'Edit Draft', disabled: false });
        }
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));
    }

    private _onMenuSelect(event) {
        const type = +event.payload.id;
        const thread = event.payload.data;
        switch (type) {
            case 0:
                this._delete(thread);
                break;
            case 1:
                this.onItemDoubleClick(event.payload.event, thread);
                break;
            default:
                break;
        }
    }

    private _onSubscribeActions(action: any) {
        if (action.type === this.config.reloadAction) {
            this._currentPageIndex = 1;
            this.reloading = true;
        }
    }

    private _onSubscribSortInfo(res: any) {
        if (res) {
            this.sortInfo = res;
        }
    }

    private _onSubscribQueryInfo(res: any) {
        if (res) {
            this._queryInfo = res;
        }
    }

    private _onSubscribeDraftListItem(items: DataList<Thread>) {
        this.draftList = items;
        if (this.reloading) {
            this.reloading = false;
            this.listContainerEle.nativeElement.parentElement.focus(); // focus on DOM to let use could directly use key up/down to switch thread
            if (this.draftList && this.draftList.length > 0) {
                // need to select the first item by default after reload
                this.selectedItemMap.clear();
                this.selectedItemMap.set(this.draftList[0].id, 0);
                this.listContainerEle.nativeElement.scrollTop = 0;
            }
        }

        if (this.loadingMore) {
            this.loadingMore = false;
        }
    }

    private _loadMoreDraftList() {
        this._store.dispatch({
            type: this.config.loadMoreAction,
            payload: {
                pageIndex: this._currentPageIndex,
                pageSize: 20,
                ...this._queryInfo
            }
        });
    }
}
